import { useMemo, useState } from 'react'
import { Plus } from 'lucide-react'
import { applyTableState, useTableState, DataTableToolbar } from '@/components/blocks/data-table'
import { Button } from '@/components/ui/Button'
import { useToast } from '@/components/ui/Toast'
import {
  CODE_COLUMNS,
  CODE_FILTERS,
  CodesTable,
  type CreateCodeValues,
  CreateCodeDialog,
} from '@/components/giftcodes'
import { ConfirmDialog, type ConfirmRequest, PageHeader, StatCard } from '@/components/shared'
import { count } from '@/lib'
import { GIFT_CODES } from '@/fixtures'
import type { GiftCode, Stat } from '@/models'

const TABLE_INITIAL = { pageSize: 10 }

export function GiftCodesPage() {
  const { toast } = useToast()
  const [codes, setCodes] = useState<GiftCode[]>(GIFT_CODES)
  const [createOpen, setCreateOpen] = useState(false)
  const [confirm, setConfirm] = useState<ConfirmRequest | null>(null)
  const { state, setState } = useTableState(TABLE_INITIAL)

  const table = useMemo(() => applyTableState(codes, CODE_COLUMNS, state), [codes, state])

  const stats: Stat[] = useMemo(() => {
    const activeCount = codes.filter((c) => c.status === 'Active').length
    const totalRedeemed = codes.reduce((sum, c) => sum + c.redeemed, 0)
    const inactiveCount = codes.filter((c) => c.status !== 'Active').length

    return [
      {
        id: 'total',
        label: 'Total promo codes',
        value: String(codes.length),
        delta: '+2 this week',
        up: true,
      },
      {
        id: 'active',
        label: 'Active codes',
        value: String(activeCount),
        delta: `${Math.round((activeCount / (codes.length || 1)) * 100)}% active`,
        up: true,
      },
      {
        id: 'redeemed',
        label: 'Total redemptions',
        value: count(totalRedeemed),
        delta: '+12.4%',
        up: true,
      },
      {
        id: 'expired',
        label: 'Expired / Exhausted',
        value: String(inactiveCount),
        delta: '−0.5%',
        up: false,
      },
    ]
  }, [codes])

  const handleCreate = (values: CreateCodeValues) => {
    setCodes((previous) => [
      {
        id: `gc_${crypto.randomUUID().slice(0, 8)}`,
        code: values.code.toUpperCase(),
        reward: values.reward,
        redeemed: 0,
        limit: values.limit,
        expires: values.expires,
        status: 'Active',
      },
      ...previous,
    ])
    toast(`Created ${values.code.toUpperCase()}`, 'success')
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Gift Codes"
        description="Promo codes players redeem in-game for rewards."
        actions={
          <Button onClick={() => setCreateOpen(true)}>
            <Plus />
            New Code
          </Button>
        }
      />

      {/* Summary Stat Micro-Cards */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {stats.map((stat) => (
          <StatCard key={stat.id} stat={stat} />
        ))}
      </div>

      {/* Search & Filter Toolbar */}
      <div className="space-y-3">
        <DataTableToolbar
          state={state}
          onStateChange={setState}
          searchPlaceholder="Search codes by string or reward…"
          filters={CODE_FILTERS}
        />

        <CodesTable
          rows={table.rows}
          total={table.total}
          state={state}
          onStateChange={setState}
          onCopy={(code) => {
            void navigator.clipboard?.writeText(code.code)
            toast(`Copied ${code.code}`)
          }}
          onRevoke={(code) =>
            setConfirm({
              title: `Revoke ${code.code}?`,
              description: 'Players can no longer redeem it. Rewards already granted are kept.',
              confirmLabel: 'Revoke Code',
              destructive: true,
              onConfirm: () => {
                setCodes((previous) => previous.filter((row) => row.id !== code.id))
                toast(`Revoked ${code.code}`, 'danger')
              },
            })
          }
        />
      </div>

      <CreateCodeDialog open={createOpen} onOpenChange={setCreateOpen} onCreate={handleCreate} />
      <ConfirmDialog request={confirm} onClose={() => setConfirm(null)} />
    </div>
  )
}
